import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { AuthLayout } from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthLayout testId="forgot-password-page" title="Reset your password" subtitle="We'll email you a secure link that expires in 1 hour."
      footer={<Link to="/login" data-testid="forgot-back-login-link" className="text-emerald-800 dark:text-emerald-400 font-semibold">Back to login</Link>}>
      {sent ? (
        <div data-testid="forgot-success" className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 p-5 text-sm">
          <p className="font-semibold text-emerald-900 dark:text-emerald-300">Check your inbox</p>
          <p className="mt-1 text-emerald-800/80 dark:text-emerald-200/80">{sent.message}</p>
          {sent.dev_reset_link && (
            <p className="mt-3 text-xs">Email delivery is in dev mode — <a data-testid="forgot-dev-reset-link" href={sent.dev_reset_link} className="underline font-medium break-all">open reset link</a></p>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 rounded-xl" /></div>
          <Button data-testid="forgot-submit-button" type="submit" disabled={loading} className="w-full h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950 font-semibold">{loading ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords do not match");
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password });
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <AuthLayout testId="reset-password-page" title="Choose a new password" subtitle="Minimum 8 characters.">
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2"><Label htmlFor="password">New password</Label><Input id="password" type="password" data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="h-11 rounded-xl" /></div>
        <div className="space-y-2"><Label htmlFor="confirm">Confirm password</Label><Input id="confirm" type="password" data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="h-11 rounded-xl" /></div>
        {error && <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 dark:bg-red-950/40 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <Button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full h-11 rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950 font-semibold">{loading ? "Updating…" : "Update password"}</Button>
      </form>
    </AuthLayout>
  );
}
